import './Button.scss';
import './Button.m.scss';
import { Component } from '@components/Component/Component.js';
import { createElement, debounce } from '@components/Utility/index.js';
import DIContainer from '@components/DependencyInjectionContainer.js';
import { StyleModifiers } from '@components/Component/Button/ButtonTypes.js';
/**
 * `Button` component intended for placing buttons on forms.
 *
 * @example
 * // create a button instance
 * const button = s360.DIContainer.new('Button', '<id>', rootHtmlElement);
 * // attach a click handler
 * button.on('click', () => {console.log('clicked');})
 * // render the button inside the rootHtmlElement you provided.
 * button.draw();
 *
 * OR
 *
 * component_somebutton(button) {
 *     // setup `button` as above.
 * }
 */
export class Button extends Component {
    get inputWidth() { return this._inputWidth; }
    set inputWidth(value) {
        this._propertySet('inputWidth', value);
        this.renderInputWidthSize();
    }
    get buttonSize() { return this._buttonSize; }
    set buttonSize(value) {
        this._propertySet('buttonSize', value);
        this.renderButtonSize();
    }
    get buttonWidth() { return this._buttonWidth; }
    set buttonWidth(value) {
        this._propertySet('buttonWidth', value);
        this.renderButtonWidth();
    }
    get buttonIcon() {
        return this._buttonIcon;
    }
    set buttonIcon(value) {
        this._propertySet('buttonIcon', value || '');
        this.renderButtonIcon();
    }
    get caption() { return this._caption || ''; }
    set caption(value) {
        this._propertySet('caption', value);
        this.renderCaption();
    }
    get tooltip() { return this._tooltip || ''; }
    set tooltip(value) {
        if ((value || '') != (this._tooltip || '')) {
            this._propertySet('tooltip', value);
            this.renderTooltip();
        }
    }
    get hint() { return this._hint || ''; }
    set hint(value) {
        this._propertySet('hint', value);
        this.renderHint();
    }
    get styleModifier() { return this._styleModifier; }
    set styleModifier(val) {
        this._propertySet('styleModifier', val);
        this.setStyleModifier();
    }
    get buttonLayout() {
        return this._buttonLayout;
    }
    set buttonLayout(value) {
        this._propertySet('buttonLayout', value);
        this.renderButtonLayout();
    }
    // there should be specific properties for specific classes, ie. button-style vs container-style and primary/secondary/tertiary if needed. Not just a random classes property
    // private _classList?: string[]
    // get classList(): string[] { return this._classList || []}
    // set classList(value: string[]) {
    // 	if (value.length) {
    // 		this._propertySet('classList', value)
    // 	}
    // 	this._classList = value;
    //
    // 	this.renderClasses()
    // }
    constructor(id, rootElement, dependencies = {}) {
        super(id, rootElement, dependencies);
        this.componentName = 'Button';
        /** Boolean. Must be set before draw. Pads the left side equal to the width of labels so that button will align with a column of inputs.  */
        this._inputWidth = false;
        this._buttonSize = '';
        this._buttonWidth = '';
        this._buttonIcon = '';
        this._buttonLayout = '';
        this.dynamicCodeEvents.click = 'click';
    }
    captionTypesSet() {
        this.captionTypes = {
            caption: 'caption',
            tooltip: 'tooltip',
            hint: 'hint'
        };
    }
    draw() {
        super.draw();
        // debounce to prevent double clicks running action twice, and to allow inputs to defocus and apply their value to the linked record.
        this.registerEvent('click', this.buttonElement, this.handleClick.bind(this));
        this.debouncedClick = debounce(this.debouncedClick, 50);
        if (this.buttonSize) {
            this.renderButtonSize();
        }
        if (this.buttonWidth) {
            this.renderButtonWidth();
        }
        if (this._inputWidth) {
            this.renderInputWidthSize();
        }
        if (this.buttonIcon) {
            this.renderButtonIcon();
        }
        if (this.hint) {
            this.renderHint();
        }
        if (this.buttonLayout) {
            this.renderButtonLayout();
        }
        this.setStyleModifier();
        // if (this.classList.length) {
        // 	this.renderClasses()
        // }
    }
    renderButtonLayout() {
        if (!this.buttonElement || !this.drawn)
            return;
        //remove existing layout classes
        this.buttonElement.classList.remove('layout-stacked', 'layout-left-aligned');
        //styling
        if (this.buttonLayout) {
            this.buttonElement.classList.add(`layout-${this.buttonLayout}`);
        }
    }
    renderButtonIcon() {
        if (!this.buttonElement || !this.drawn)
            return;
        if (this.iconElement) {
            this.iconElement.remove();
            this.iconElement = undefined;
        }
        // Get icon
        if (this.buttonIcon) {
            const iconManager = DIContainer.getInstance('IconManager');
            const i = iconManager.getIconElement(this.buttonIcon);
            if (i) {
                this.iconElement = i;
                this.iconElement.classList.add('button-icon');
                // Put icon before the text
                this.buttonElement.prepend(this.iconElement);
            }
        }
    }
    renderHint() {
        if (!this.buttonElement || !this.drawn || !this.textContainerElement)
            return;
        if (this.hint) {
            if (!this.hintElement) {
                this.hintElement = createElement('span', ['button-hint']);
                this.textContainerElement.appendChild(this.hintElement);
            }
            this.hintElement.textContent = this.hint;
            this.buttonElement.classList.add('has-hint');
        }
        else {
            this.hintElement?.remove();
            this.hintElement = undefined;
            this.buttonElement.classList.remove('has-hint');
        }
    }
    renderInputWidthSize() {
        if (this.drawn) {
            if (this._inputWidth) {
                this.rootElement.classList.add('inputWidth');
            }
            else {
                this.rootElement.classList.remove('inputWidth');
            }
        }
    }
    renderButtonSize() {
        if (this.drawn) {
            // Create a regular expression to match classes that exigence it with 'button-size-'
            const classRegex = /button-size-\S+/;
            //Remove classes that match the regex from this.rootElement
            this.rootElement.classList.forEach(className => {
                if (classRegex.test(className)) {
                    this.rootElement.classList.remove(className);
                }
            });
            if (this._buttonSize) {
                this.rootElement.classList.add(`button-size-${this._buttonSize}`);
            }
        }
    }
    renderButtonWidth() {
        if (this.drawn) {
            // Create a regular expression to match classes that exigence it with 'button-width-'
            const classRegex = /button-width-\S+/;
            //Remove classes that match the regex from this.rootElement
            this.rootElement.classList.forEach(className => {
                if (classRegex.test(className)) {
                    this.rootElement.classList.remove(className);
                }
            });
            if (this._buttonWidth) {
                this.rootElement.classList.add(`button-width-${this._buttonWidth}`);
            }
        }
    }
    renderCaption() {
        if (this.captionElement) {
            this.captionElement.textContent = this.caption;
        }
    }
    renderTooltip() {
        if (this.buttonElement) {
            this.registerTooltip(this.buttonElement, {
                tooltip: this.tooltip
            });
        }
    }
    // private renderClasses() {
    // 	if (this.buttonElement) {
    // 		this.classList.forEach((className) => this.buttonElement?.classList.add(className))
    // 	}
    // }
    /**
     * Utility function to programmatically click the button.
     */
    click(e) {
        this.trigger('click', {
            event: e
        }, e);
    }
    debouncedClick(e) {
        console.log('Button debouncedClick');
        this.click(e);
    }
    /**
     * Click event handler
     * @param e - The mouse event
     */
    handleClick(e) {
        if (this.formDesignerMode)
            return;
        // need to force a blur on the focused component because of timing issues encountered where sometimes clicks run before blurs (even though the act of clicking should blur the input first)
        // and we need the blur to run first to ensure that if someone was in the middle of typing a value in an input, that the value is set in the linkedRecord before running our click.
        const app = DIContainer.getInstance('Application');
        app.blurFocusedComponent();
        app.trackTelemetry({
            type: 'button_click',
            componentId: this.id,
            formName: this.formContext?.formName || '',
            metadata: {
                caption: this.caption
            }
        });
        e.preventDefault();
        this.debouncedClick(e);
    }
    renderDisabledState(isDisabled) {
        super.renderDisabledState(isDisabled);
        if (this.buttonElement) {
            this.buttonElement.disabled = isDisabled;
        }
    }
    setStyleModifier() {
        if (this.buttonElement) {
            StyleModifiers.forEach((style) => {
                this.buttonElement.classList.toggle(style, style === this.styleModifier);
            });
        }
    }
    template() {
        this.buttonElement = createElement('button', ['button-style']);
        //Create the wrapper for text group
        this.textContainerElement = createElement('div', ['button-text-container']);
        //Create the caption span and put it in the wrapper
        this.captionElement = createElement('span', ['button-text']);
        this.textContainerElement.appendChild(this.captionElement);
        this.buttonElement.appendChild(this.textContainerElement);
        if (this._caption) {
            this.renderCaption();
        }
        this.rootElement.appendChild(this.buttonElement);
        return '';
    }
    formDesignerPropertiesGet(properties) {
        properties = super.formDesignerPropertiesGet(properties);
        properties.inputWidth = ({
            id: 'inputWidth',
            value: this.inputWidth,
            componentType: 'InputCheckbox',
            componentProperties: {
                checkedValue: true,
                unCheckedValue: false,
                labelWidth: 'auto'
            },
            captionKey: 'formbuilder_button_inputwidth',
            propertyGroup: '',
            saveFlag: () => this.inputWidth
        });
        properties.buttonSize = ({
            id: 'buttonSize',
            value: this.buttonSize,
            componentType: 'Select',
            componentProperties: {
                staticOptionData: [
                    { value: '', display: 'Normal' },
                    { value: 'large', display: 'Large' },
                    { value: 'xlarge', display: 'Extra Large' }
                ]
            },
            captionKey: 'formbuilder_button_buttonsize',
            propertyGroup: '',
            saveFlag: () => !!this.buttonSize
        });
        properties.buttonWidth = ({
            id: 'buttonWidth',
            value: this.buttonWidth,
            componentType: 'Select',
            componentProperties: {
                staticOptionData: [
                    { value: '', display: 'Normal' },
                    { value: 'full', display: 'Full Width' }
                ]
            },
            captionKey: 'formbuilder_button_buttonwidth',
            propertyGroup: '',
            saveFlag: () => !!this.buttonWidth
        });
        properties.buttonIcon = ({
            id: 'buttonIcon',
            value: this.buttonIcon,
            componentType: 'InputIcon',
            componentProperties: {},
            captionKey: 'formbuilder_button_buttonicon',
            propertyGroup: '',
            saveFlag: () => !!this.buttonIcon
        });
        properties.styleModifier = ({
            id: 'styleModifier',
            value: this.styleModifier,
            componentType: 'Select',
            componentProperties: {
                staticOptionData: [
                    { value: '', display: 'Default' },
                    { value: 'container-style', display: 'Container Style' },
                    { value: 'secondary', display: 'Secondary' },
                    { value: 'destructive', display: 'Destructive' },
                    { value: 'destructive-secondary', display: 'Destructive Secondary' },
                    { value: 'info', display: 'Info' },
                    { value: 'info-secondary', display: 'Info Secondary' },
                    { value: 'tertiary', display: 'Tertiary' }
                ]
            },
            captionKey: 'formbuilder_button_buttonstyle',
            propertyGroup: '',
            saveFlag: () => !!this.styleModifier
        });
        properties.buttonLayout = ({
            id: 'buttonLayout',
            value: this.buttonLayout,
            componentType: 'Select',
            componentProperties: {
                staticOptionData: [
                    { value: '', display: 'Default' },
                    { value: 'left-aligned', display: 'Left Aligned' }
                ]
            },
            captionKey: 'formbuilder_button_buttonlayout',
            propertyGroup: '',
            saveFlag: () => !!this.buttonLayout
        });
        return properties;
    }
    destroy() {
        super.destroy();
        this.iconElement = undefined;
        this.buttonElement = undefined;
        this.hintElement = undefined;
    }
}
Button.VERSION = '$Revision: 130578 $';
